/**
 * ConstraintLayer — renders constraint indicators on edges.
 *
 * Symbols drawn at the wall midpoint (or near the shared node for 2-edge constraints):
 *   LOCK_LENGTH   → padlock icon  🔒 (drawn as SVG path)
 *   HORIZONTAL    → H badge
 *   VERTICAL      → V badge
 *   PERPENDICULAR → ⊥ symbol at shared node
 *   PARALLEL      → ∥ symbol at midpoint of each edge
 *   LOCK_ANGLE    → angle arc symbol
 *
 * All symbols scale with zoom (izoom) and are faint unless the edge is selected.
 */
import React from "react";
import { w2pt, w2p, izoom } from "../../utils/coords";
import { Geo } from "../../utils/geo";

function edgeEndpoints(eId, st) {
  const e = st.edges[eId]; if (!e) return null;
  const ab = st.halfEdges[e.halfEdgeA], ba = st.halfEdges[e.halfEdgeB];
  if (!ab || !ba) return null;
  const nA = st.nodes[ab.originId], nB = st.nodes[ba.originId];
  if (!nA || !nB) return null;
  return { pA: w2pt(nA), pB: w2pt(nB), nA, nB };
}

function edgeMid(ep) {
  return { x: (ep.pA.x + ep.pB.x) / 2, y: (ep.pA.y + ep.pB.y) / 2 };
}

// Padlock SVG path (simplified, centred at 0,0, radius ~r)
function Padlock({ x, y, r, col }) {
  const sw = r * 0.18;
  return (
    <g transform={`translate(${x},${y})`} style={{ pointerEvents: "none" }}>
      {/* shackle arc */}
      <path d={`M ${-r*0.4} ${-r*0.1} L ${-r*0.4} ${-r*0.55} A ${r*0.4} ${r*0.4} 0 0 1 ${r*0.4} ${-r*0.55} L ${r*0.4} ${-r*0.1}`}
        fill="none" stroke={col} strokeWidth={sw} strokeLinecap="round"/>
      {/* body */}
      <rect x={-r*0.55} y={-r*0.15} width={r*1.1} height={r*0.85} rx={r*0.15}
        fill={col + "30"} stroke={col} strokeWidth={sw*0.8}/>
      {/* keyhole */}
      <circle cx={0} cy={r*0.22} r={r*0.14} fill={col} opacity={0.8}/>
      <rect x={-r*0.06} y={r*0.22} width={r*0.12} height={r*0.22} rx={r*0.04} fill={col} opacity={0.8}/>
    </g>
  );
}

// ⊥ symbol
function PerpSymbol({ x, y, r, col, angle }) {
  const L = r * 0.9;
  return (
    <g transform={`translate(${x},${y}) rotate(${angle})`} style={{ pointerEvents: "none" }}>
      <line x1={-L} y1={0} x2={L}  y2={0}  stroke={col} strokeWidth={r*0.18} strokeLinecap="round"/>
      <line x1={0}  y1={0} x2={0}  y2={-L} stroke={col} strokeWidth={r*0.18} strokeLinecap="round"/>
      <line x1={-L*0.35} y1={-L*0.35} x2={0} y2={-L*0.35} stroke={col} strokeWidth={r*0.15} strokeLinecap="round"/>
      <line x1={0} y1={-L*0.35} x2={0} y2={0} stroke={col} strokeWidth={r*0.15} strokeLinecap="round"/>
    </g>
  );
}

// ∥ symbol
function ParallelSymbol({ x, y, r, col, angle }) {
  const L = r * 0.7;
  const gap = r * 0.28;
  return (
    <g transform={`translate(${x},${y}) rotate(${angle})`} style={{ pointerEvents: "none" }}>
      <line x1={-gap} y1={-L} x2={-gap} y2={L} stroke={col} strokeWidth={r*0.18} strokeLinecap="round"/>
      <line x1={ gap} y1={-L} x2={ gap} y2={L} stroke={col} strokeWidth={r*0.18} strokeLinecap="round"/>
    </g>
  );
}

// Badge (H / V / °)
function Badge({ x, y, r, col, text }) {
  return (
    <g transform={`translate(${x},${y})`} style={{ pointerEvents: "none" }}>
      <rect x={-r*0.7} y={-r*0.55} width={r*1.4} height={r*1.1} rx={r*0.25}
        fill={col + "25"} stroke={col} strokeWidth={r*0.14}/>
      <text x={0} y={r*0.32} textAnchor="middle" fontSize={r*0.9}
        fill={col} fontFamily="'JetBrains Mono',monospace" fontWeight={700}>
        {text}
      </text>
    </g>
  );
}

export default function ConstraintLayer({ st, selEdgeId, zoom }) {
  const constraints = Object.values(st.constraints || {}).filter(c => c.active !== false);
  if (!constraints.length) return null;

  const s = izoom(zoom);
  const R = s * 9; // symbol radius in screen px
  const dimLayer = (st.layers || {}).dimensions;
  if (dimLayer?.visible === false) return null;

  const elements = [];

  for (const con of constraints) {
    const isSel = con.entities.some(eId => eId === selEdgeId);
    const col   = isSel ? "#e8a838" : "#60a5fa";
    const alpha = isSel ? "ff" : "bb";

    if (con.type === "LOCK_LENGTH") {
      const ep = edgeEndpoints(con.entities[0], st); if (!ep) continue;
      const mid = edgeMid(ep);
      const dx = ep.pB.x - ep.pA.x, dy = ep.pB.y - ep.pA.y, L = Math.sqrt(dx*dx+dy*dy) || 1;
      // Offset above midline
      const off = R * 2;
      const mx = mid.x + (-dy/L) * off;
      const my = mid.y + ( dx/L) * off;
      elements.push(<Padlock key={con.id} x={mx} y={my} r={R} col={col + alpha}/>);
    }

    else if (con.type === "HORIZONTAL") {
      const ep = edgeEndpoints(con.entities[0], st); if (!ep) continue;
      const mid = edgeMid(ep);
      elements.push(<Badge key={con.id} x={mid.x} y={mid.y - R*2.2} r={R} col={col+alpha} text="H"/>);
    }

    else if (con.type === "VERTICAL") {
      const ep = edgeEndpoints(con.entities[0], st); if (!ep) continue;
      const mid = edgeMid(ep);
      elements.push(<Badge key={con.id} x={mid.x + R*2.2} y={mid.y} r={R} col={col+alpha} text="V"/>);
    }

    else if (con.type === "PERPENDICULAR") {
      const [eIdA, eIdB] = con.entities;
      const epA = edgeEndpoints(eIdA, st), epB = edgeEndpoints(eIdB, st);
      if (!epA || !epB) continue;
      // Find shared node (if any) — draw symbol there, else at midA
      const sharedPt =
        (epA.nA.x === epB.nA.x && epA.nA.y === epB.nA.y) ? epA.pA :
        (epA.nA.x === epB.nB.x && epA.nA.y === epB.nB.y) ? epA.pA :
        (epA.nB.x === epB.nA.x && epA.nB.y === epB.nA.y) ? epA.pB :
        (epA.nB.x === epB.nB.x && epA.nB.y === epB.nB.y) ? epA.pB :
        edgeMid(epA);
      const dxA = epA.pB.x - epA.pA.x, dyA = epA.pB.y - epA.pA.y;
      const ang = Math.atan2(dyA, dxA) * 180 / Math.PI;
      elements.push(<PerpSymbol key={con.id} x={sharedPt.x} y={sharedPt.y} r={R*1.2} col={col+alpha} angle={ang}/>);
    }

    else if (con.type === "PARALLEL") {
      const [eIdA, eIdB] = con.entities;
      const epA = edgeEndpoints(eIdA, st), epB = edgeEndpoints(eIdB, st);
      if (!epA || !epB) continue;
      const midA = edgeMid(epA), midB = edgeMid(epB);
      const dxA = epA.pB.x - epA.pA.x, dyA = epA.pB.y - epA.pA.y;
      const angA = Math.atan2(dyA, dxA) * 180 / Math.PI;
      elements.push(
        <ParallelSymbol key={con.id+"A"} x={midA.x} y={midA.y} r={R} col={col+alpha} angle={angA}/>,
        <ParallelSymbol key={con.id+"B"} x={midB.x} y={midB.y} r={R} col={col+alpha} angle={angA}/>,
      );
    }

    else if (con.type === "LOCK_ANGLE") {
      const [eIdA] = con.entities;
      const ep = edgeEndpoints(eIdA, st); if (!ep) continue;
      const mid = edgeMid(ep);
      const deg = Math.round(con.params?.angle || 0);
      elements.push(<Badge key={con.id} x={mid.x} y={mid.y - R*2.2} r={R} col={col+alpha} text={`${deg}°`}/>);
    }
  }

  return <g>{elements}</g>;
}
